/**
 * Unified Hotspot Component System
 * Handles both two-up and full-width hotspot sections
 */

if (!customElements.get('component-hotspots')) {
  class ComponentHotspots extends HTMLElement {
    constructor() {
      super();
      
      this.data = this.dataset;
      this.sectionType = this.data.sectionType || 'two-up'; // 'two-up' or 'full-width'
      this.mobileMenu = this.querySelector('.hotspot-mobile-menu');
      this.mobileProducts = this.querySelector('.hotspot-mobile-menu__products');
      this.mobileCtaBtn = this.querySelector(`#mobile-cta-btn-${this.data.sectionId}`);
      this.mobileCloseButtons = [...this.querySelectorAll('[data-close-menu]')];
      this.allProducts = [];
      this.currentBlock = null;
      
      this.isMobile = () => window.innerWidth <= 767;
      this.formatMoney = (cents) => '$' + (cents / 100).toFixed(2);
    }
    
    connectedCallback() {
      // Use setTimeout to ensure child components are initialized first
      setTimeout(() => {
        this.initializeHotspots();
        this.bindEvents();
        this.updateHotspotPositions();
      }, 0);
    }
    
    initializeHotspots() {
      // Initialize child components based on section type
      const childComponents = this.getChildComponents();
      childComponents.forEach(component => {
        component.parentHotspots = this;
      });
      
      // Collect all products from child blocks
      this.collectProducts();
    }
    
    getChildComponents() {
      // Return all hotspot block components regardless of section type
      return [...this.querySelectorAll('component-hotspot-block')];
    }
    
    collectProducts(specificBlock = null) {
      this.allProducts = [];
      
      if (specificBlock) {
        // Collect products only from the specific block
        if (specificBlock.getProducts && typeof specificBlock.getProducts === 'function') {
          const products = specificBlock.getProducts();
          this.allProducts.push(...products);
        } else {
          console.warn('Specific block does not have getProducts method or is not ready yet');
        }
      } else {
        // Collect products from all child blocks (for initial setup)
        const childBlocks = this.getChildComponents();
        childBlocks.forEach(block => {
          if (block.getProducts && typeof block.getProducts === 'function') {
            const products = block.getProducts();
            this.allProducts.push(...products);
          } else {
            console.warn('Block getProducts method not available yet, will retry');
          }
        });
      }
    }
    
    bindEvents() {
      // Close mobile menu events
      this.mobileCloseButtons.forEach(button => {
        button.addEventListener('click', () => {
          this.hideMobileMenu();
        });
      });
      
      // Responsive positioning on resize
      window.addEventListener('resize', () => {
        this.updateHotspotPositions();
      });
      
      // Close mobile menu on overlay click
      if (this.mobileMenu) {
        const overlay = this.mobileMenu.querySelector('.hotspot-mobile-menu__overlay');
        if (overlay) {
          overlay.addEventListener('click', () => {
            this.hideMobileMenu();
          });
        }
      }
    }
    
    showMobileMenu() {
      if (this.mobileProducts && this.allProducts.length > 0) {
        let productListHTML = '';
        
        this.allProducts.forEach(product => {
          const priceHTML = product.compare_at_price > product.price 
            ? `<span class="price price--sale">${this.formatMoney(product.price)}</span><span class="price price--compare">${this.formatMoney(product.compare_at_price)}</span>`
            : `<span class="price">${this.formatMoney(product.price)}</span>`;
            
          productListHTML += `
            <a href="${product.url}" class="hotspot-mobile-menu__product-item">
              ${product.featured_image ? `
                <div class="hotspot-mobile-menu__product-image">
                  <img src="${product.featured_image}" alt="${product.featured_image_alt || ''}" width="106" height="106" loading="lazy">
                </div>
              ` : ''}
              <div class="hotspot-mobile-menu__product-details">
                <h4 class="hotspot-mobile-menu__product-title">${product.title}</h4>
                <div class="hotspot-mobile-menu__product-price">${priceHTML}</div>
                <span class="hotspot-mobile-menu__view-details btn btn--link">View Details</span>
              </div>
            </a>
          `;
        });
        
        this.mobileProducts.innerHTML = productListHTML;
        
        // Update CTA button if we have a current block
        if (this.currentBlock && this.mobileCtaBtn) {
          const ctaData = this.currentBlock.getCtaData();
          this.mobileCtaBtn.textContent = ctaData.label;
          this.mobileCtaBtn.href = ctaData.url;
        }
        
        // Disable page scroll
        document.documentElement.classList.add('hotspot-flyout-open');
        
        // Show menu with animation
        this.mobileMenu.style.display = 'block';
        this.mobileMenu.classList.add('hotspot-mobile-menu--opening');
        
        // Force reflow to ensure the opening class is applied
        this.mobileMenu.offsetHeight;
        
        // Add the open class for animation
        setTimeout(() => {
          this.mobileMenu.classList.add('hotspot-mobile-menu--open');
          this.mobileMenu.classList.remove('hotspot-mobile-menu--opening');
        }, 10);
      }
    }
    
    hideMobileMenu() {
      if (this.mobileMenu) {
        this.mobileMenu.classList.add('hotspot-mobile-menu--closing');
        this.mobileMenu.classList.remove('hotspot-mobile-menu--open');
        
        // Restore page scroll
        document.documentElement.classList.remove('hotspot-flyout-open');
        
        // Hide after animation completes
        setTimeout(() => {
          this.mobileMenu.style.display = 'none';
          this.mobileMenu.classList.remove('hotspot-mobile-menu--closing');
        }, 300); // Match the CSS transition duration
      }
    }
    
    updateHotspotPositions() {
      const allHotspots = [...this.querySelectorAll('.hotspot')];
      allHotspots.forEach(hotspot => {
        const isMobile = this.isMobile();
        const x = isMobile ? hotspot.dataset.mobileX : hotspot.dataset.desktopX;
        const y = isMobile ? hotspot.dataset.mobileY : hotspot.dataset.desktopY;
        
        if (x && y) {
          hotspot.style.left = x + '%';
          hotspot.style.top = y + '%';
        }
      });
    }
    
    // Public method for child blocks to trigger mobile menu
    triggerMobileMenu(block) {
      this.currentBlock = block;
      this.collectProducts(block); // Collect products only from this specific block
      
      // If no products collected (due to timing), try again after a short delay
      if (this.allProducts.length === 0) {
        setTimeout(() => {
          this.collectProducts(block);
          this.showMobileMenu();
        }, 50);
      } else {
        this.showMobileMenu();
      }
    }
  }
  
  customElements.define('component-hotspots', ComponentHotspots);
}

/**
 * Unified Hotspot Block Component
 * Handles both CTA blocks (two-up) and content blocks (full-width)
 */
if (!customElements.get('component-hotspot-block')) {
  class ComponentHotspotBlock extends HTMLElement {
    constructor() {
      super();
      
      this.data = this.dataset;
      this.blockType = this.data.blockType || 'cta'; // 'cta' or 'content'
      this.parentHotspots = null;
      this.hotspots = [...this.querySelectorAll('.hotspot')];
      this.products = [];
      
      this.isMobile = () => window.innerWidth <= 767;
    }
    
    connectedCallback() {
      this.initializeHotspots();
      this.bindHotspotEvents();
      this.collectProducts();
    }
    
    initializeHotspots() {
      this.hotspots.forEach(hotspot => {
        hotspot.blockParent = this;
      });
    }
    
    collectProducts() {
      this.products = [];
      
      // Collect products from hotspots
      this.hotspots.forEach(hotspot => {
        const productDataScript = hotspot.querySelector('.hotspot__product-data');
        if (productDataScript) {
          try {
            const productData = JSON.parse(productDataScript.textContent);
            this.products.push(productData);
          } catch (e) {
            console.warn('Failed to parse product data:', e);
          }
        }
      });
    }
    
    getCtaData() {
      // Get block button data from the text content area
      const ctaButton = this.querySelector('.hotspot-block__content .btn, .hotspot-content-block__content .btn');
      return {
        label: ctaButton ? ctaButton.textContent.trim() : 'Shop Collection',
        url: ctaButton ? ctaButton.getAttribute('href') : '/collections/all'
      };
    }
    
    bindHotspotEvents() {
      this.hotspots.forEach(hotspot => {
        const button = hotspot.querySelector('.hotspot__button');
        const productInfo = hotspot.querySelector('.hotspot__product-info--desktop');
        const productUrl = button ? button.getAttribute('data-product-url') : null;
        
        if (button) {
          if (this.isMobile()) {
            // Mobile: Tap hotspot button to show fly-up menu
            button.addEventListener('click', (e) => {
              e.stopPropagation();
              if (this.parentHotspots) {
                this.parentHotspots.triggerMobileMenu(this);
              }
            });
          } else {
            // Desktop: Click hotspot button to show/hide popup
            let isOpen = false;
            
            const showPopup = () => {
              if (productInfo && !isOpen) {
                // Close other open popups in this block
                this.hotspots.forEach(otherHotspot => {
                  if (otherHotspot !== hotspot) {
                    const otherInfo = otherHotspot.querySelector('.hotspot__product-info--desktop');
                    if (otherInfo) {
                      otherInfo.style.opacity = '0';
                      otherInfo.style.visibility = 'hidden';
                      otherInfo.style.transform = 'translateX(-50%) translateY(0)';
                      otherHotspot.classList.remove('hotspot--active');
                    }
                  }
                });
                
                productInfo.style.opacity = '1';
                productInfo.style.visibility = 'visible';
                productInfo.style.transform = 'translateX(-50%) translateY(-5px)';
                hotspot.classList.add('hotspot--active');
                isOpen = true;
              }
            };
            
            const hidePopup = () => {
              if (productInfo && isOpen) {
                productInfo.style.opacity = '0';
                productInfo.style.visibility = 'hidden';
                productInfo.style.transform = 'translateX(-50%) translateY(0)';
                hotspot.classList.remove('hotspot--active');
                isOpen = false;
              }
            };
            
            // Toggle on click only
            button.addEventListener('click', (e) => {
              e.stopPropagation();
              if (isOpen) {
                hidePopup();
              } else {
                showPopup();
              }
            });
            
            // Click on popup goes to product page
            if (productInfo) {
              productInfo.addEventListener('click', (e) => {
                if (productUrl) {
                  window.location.href = productUrl;
                }
              });
            }
            
            // Close popup when clicking outside
            document.addEventListener('click', (e) => {
              if (!hotspot.contains(e.target)) {
                hidePopup();
              }
            });
          }
        }
      });
    }
    
    // Public method to get products for parent component
    getProducts() {
      return this.products;
    }
    
    // Refresh hotspots if DOM changes
    refreshHotspots() {
      this.hotspots = [...this.querySelectorAll('.hotspot')];
      this.initializeHotspots();
      this.bindHotspotEvents();
      this.collectProducts();
    }
  }
  
  customElements.define('component-hotspot-block', ComponentHotspotBlock);
}